import React from 'react';
import { Link } from 'react-router-dom';
import { Share2, MapPin, TrendingUp } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

import { LivingInAreaData } from '../../types/livingInArea';

interface HeroSectionProps {
  data: LivingInAreaData;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ data }) => {
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = `Living in ${data.areaName} | RentSmart.ie`;

  return (
    <header className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-cyan-900 to-teal-900 text-white pt-16 pb-20 px-4">
      {/* Blur background effects */}
      <div className="absolute -top-24 -left-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
      <div className="absolute -bottom-20 -right-10 h-80 w-80 rounded-full bg-emerald-400/20 blur-3xl" />

      <div className="relative max-w-6xl mx-auto">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-4">
          <MapPin className="h-4 w-4" /> Area Guide
        </div>

        {/* Main headline and summary */}
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
        {data.seo.title}
        </h1>
        <p className="max-w-3xl text-cyan-100 text-lg leading-relaxed mb-8">
          {data.lifestyleOverview.summary}
        </p>

        {/* Social share buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
          <span className="flex items-center gap-2 text-cyan-100 text-sm font-medium">
            <Share2 className="h-4 w-4" /> Share:
          </span>
          <div className="flex gap-3">
            <FacebookShareButton url={canonicalUrl}>
              <FacebookIcon
                size={32}
                round
                className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
              />
            </FacebookShareButton>
            <TwitterShareButton url={canonicalUrl} title={shareTitle}>
              <TwitterIcon
                size={32}
                round
                className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
              />
            </TwitterShareButton>
            <WhatsappShareButton url={canonicalUrl} title={shareTitle}>
              <WhatsappIcon
                size={32}
                round
                className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
              />
            </WhatsappShareButton>
            <LinkedinShareButton url={canonicalUrl} title={shareTitle}>
              <LinkedinIcon
                size={32}
                round
                className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
              />
            </LinkedinShareButton>
          </div>
        </div>

        {/* Key stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4">
            <div className="text-cyan-100 text-xs uppercase mb-1">Vibe</div>
            <div className="text-lg font-bold">{data.locationContext.overallCharacter}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4">
            <div className="text-cyan-100 text-xs uppercase mb-1">Setting</div>
            <div className="text-lg font-bold">{data.locationContext.setting}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4">
            <div className="text-cyan-100 text-xs uppercase mb-1">Development</div>
            <div className="text-lg font-bold">{data.locationContext.developmentStyle}</div>
          </div>
        </div>

        {/* Call-to-action buttons */}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={`/browse?location=${encodeURIComponent(data.areaName)}`}
            className="no-underline inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
          >
            <TrendingUp className="h-4 w-4" /> Browse Homes
          </Link>
        </div>
      </div>
    </header>
  );
};
